import {
  initOtpVerification,
  verifyOtp,
} from "@/features/auth/services/otpVerification";
import { isPast, parseISO } from "date-fns";
import type { StateCreator, StoreApi, UseBoundStore } from "zustand";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { Maybe, Nullable } from "types/utilities";

import type {
  OtpFlowName,
  OtpVerificationChannel,
  OtpVerificationPurpose,
} from "../utils/types";

export type OtpStatus =
  | "idle"
  | "codeSent"
  | "retrying"
  | "verified"
  | "locked"
  | "invalid";

export type OtpSession = {
  destination: string;
  verificationRequestId: string;
  retryAfter: Date;
  expiresAt: Date;
  remainingAttempts: Maybe<number>;
};

export type OtpStore = {
  status: OtpStatus;
  session: Nullable<OtpSession>;
  errorMessage: string;
  isSubmitting: boolean;

  sendOtp: (
    destination: string | undefined,
    channel: OtpVerificationChannel,
    purpose: OtpVerificationPurpose,
    authToken: Nullable<string>,
  ) => Promise<void>;
  submitOtp: (
    otpCode: string,
    channel: OtpVerificationChannel,
    authToken: Nullable<string>,
    onComplete?: (reqId: Nullable<string>, device: Nullable<string>) => void,
  ) => Promise<void>;
  autoSendOtp: (opts: {
    destination?: string;
    shouldAutoSend: boolean;
    channel?: OtpVerificationChannel;
    purpose?: OtpVerificationPurpose;
    authToken?: Nullable<string>;
  }) => void;

  clearRetryTimer: () => void;
  setError: (msg: string) => void;
  expire: () => void;

  clean: () => void; // safe
  reset: () => void; // not safe
};

const DEFAULT_ERROR = "Having trouble verifying your device?";
const EXPIRED_ERROR = "Your code expired, please try again.";

export const createOtpStoreState: StateCreator<OtpStore> = (set, get) => ({
  status: "idle",
  session: null,
  errorMessage: "",
  isSubmitting: false,

  sendOtp: async (destination, channel, purpose, authToken) => {
    if (!destination) return;
    if (channel !== "SMS" && !authToken) return;

    set({ isSubmitting: true, errorMessage: "" });

    const result = await initOtpVerification(
      destination,
      channel,
      purpose,
      authToken,
    );

    if (!result.ok) {
      get().setError(DEFAULT_ERROR);
      return;
    }

    const { verificationRequestId, retryAfter, expiresAt, remainingAttempts } =
      result.data;

    set({
      status: "codeSent",
      isSubmitting: false,
      session: {
        destination,
        verificationRequestId,
        retryAfter: parseISO(retryAfter),
        expiresAt: parseISO(expiresAt),
        remainingAttempts: remainingAttempts ?? undefined,
      },
    });
  },

  submitOtp: async (otpCode, channel, authToken, onComplete) => {
    const { session, setError } = get();
    if (!session) return;
    if (channel !== "SMS" && !authToken) return;

    set({ isSubmitting: true, errorMessage: "" });
    const requestId = session.verificationRequestId;

    const result = await verifyOtp(
      session.destination,
      otpCode,
      requestId,
      channel,
      authToken,
    );

    if (!result.ok) {
      setError(DEFAULT_ERROR);
      return;
    }

    const latest = get();
    if (!latest.session || latest.session.verificationRequestId !== requestId) {
      setError(EXPIRED_ERROR);
      return;
    }

    const { status: verifyStatus, remainingAttempts } = result.data;
    const attemptsLeft = remainingAttempts ?? 0;

    if (verifyStatus === "SUCCESS") {
      set({ status: "verified", errorMessage: "", isSubmitting: false });
      onComplete?.(requestId, session.destination);
      return;
    }

    if (verifyStatus === "FAILURE" && remainingAttempts > 0) {
      set({
        status: "retrying",
        isSubmitting: false,
        session: { ...latest.session, remainingAttempts: attemptsLeft },
      });
      return;
    }

    const now = new Date();
    const isLocked = now < latest.session.retryAfter;
    set({
      status: isLocked ? "locked" : "invalid",
      errorMessage: DEFAULT_ERROR,
      isSubmitting: false,
      session: { ...latest.session, remainingAttempts: 0 },
    });
  },

  autoSendOtp: (opts) => {
    const { status, session, sendOtp } = get();
    if (
      !opts.shouldAutoSend ||
      !opts.destination ||
      status !== "idle" ||
      session
    ) {
      return;
    }

    sendOtp(
      opts.destination,
      opts.channel!,
      opts.purpose!,
      opts.authToken ?? null,
    );
  },

  clearRetryTimer: () => {
    const { session, status } = get();
    if (!session) return;

    set({
      status: status === "locked" ? "invalid" : status,
      session: { ...session, retryAfter: new Date(0) },
    });
  },

  expire: () => get().setError(EXPIRED_ERROR),

  setError: (msg: string) =>
    set({ status: "invalid", errorMessage: msg, isSubmitting: false }),

  reset: () =>
    set({
      status: "idle",
      session: null,
      errorMessage: "",
      isSubmitting: false,
    }),

  clean: () => {
    const { status, reset } = get();
    if (["invalid", "verified"].includes(status)) reset();
  },
});

export const createPersistedOtpStore = (key: OtpFlowName) =>
  create(
    persist(createOtpStoreState, {
      name: `otp-${key}`,
      storage: createJSONStorage(() => sessionStorage),

      partialize: (s) => {
        const session = s.session;
        const hasAttempts = (session?.remainingAttempts ?? 0) > 0;
        const isExpired = session?.expiresAt && isPast(session.expiresAt);

        switch (true) {
          case s.status === "locked":
            // rate limit edge case, only unlocked by clearRetryTimer()
            return { status: "locked", session };

          case !session:
            return { status: "idle", session: null };

          case isExpired:
            return { status: "invalid", session: null };

          case s.status === "retrying":
            return { status: "retrying", session };

          case s.status === "codeSent":
            return { status: "codeSent", session };

          default:
            return {
              status: hasAttempts ? "codeSent" : "invalid",
              session: hasAttempts ? session : null,
            };
        }
      },

      onRehydrateStorage: (state) => {
        if (!state?.session) {
          state.status = "idle";
          return;
        }

        state.session.retryAfter = new Date(state.session.retryAfter);
        state.session.expiresAt = new Date(state.session.expiresAt);

        if (isPast(state.session.expiresAt)) {
          state.status = "invalid";
          state.session = null;
        }
      },
    }),
  );

const otpStoreMap: Partial<
  Record<OtpFlowName, UseBoundStore<StoreApi<OtpStore>>>
> = {};

export const getOtpFlowStore = (flow: OtpFlowName) => {
  if (!otpStoreMap[flow]) {
    otpStoreMap[flow] =
      flow === "signup"
        ? create(createOtpStoreState)
        : createPersistedOtpStore(flow);
  }

  return otpStoreMap[flow];
};
